'use client';

import { memo } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import Link from 'next/link';
import { cn } from '../../utils/cn';
import { BlurImage } from '../ui';
import { caseImageSrcSet } from '../../lib/caseImage';

export interface CaseCardBaseProps {
  imageUrl: string;
  imagePlaceholder?: string | null;
  alt?: string;
  href?: string;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  topSlot?: ReactNode;
  bottomSlot?: ReactNode;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'auto';
  imageSizes?: string;
}

const CaseCardBaseComponent = ({
  imageUrl,
  imagePlaceholder,
  alt,
  href,
  onClick,
  className,
  style,
  topSlot,
  bottomSlot,
  loading,
  fetchPriority,
  imageSizes,
}: CaseCardBaseProps) => {
  const rootClassName = cn('bg-[rgba(30,30,40,0.85)] rounded-2xl p-0 cursor-pointer overflow-hidden aspect-square', className);
  // WHY: уменьшенную копию отдаём только там, где сетка знает ширину карточки, — без sizes браузер взял бы самую широкую
  const srcSet = imageSizes ? caseImageSrcSet(imageUrl) : undefined;
  const content = (
    <div className="relative w-full h-full">
      <BlurImage
        src={imageUrl}
        srcSet={srcSet}
        sizes={srcSet ? imageSizes : undefined}
        placeholder={imagePlaceholder}
        alt={alt || ''}
        loading={loading}
        fetchPriority={fetchPriority}
        className="absolute inset-0"
      />

      {topSlot && <div className="absolute top-2 end-2 z-[1]">{topSlot}</div>}

      {bottomSlot && <div className="absolute bottom-0 start-0 end-0 p-3 flex flex-col items-center gap-1 z-[1]">{bottomSlot}</div>}
    </div>
  );

  if (href) {
    return (
      <Link href={href} prefetch={false} className={cn('block', rootClassName)} style={style}>
        {content}
      </Link>
    );
  }

  return (
    <div className={rootClassName} style={style} onClick={onClick}>
      {content}
    </div>
  );
};

export const CaseCardBase = memo(CaseCardBaseComponent);
